import React, { useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { faPaperPlane } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { dispatchAnalyticsTrack } from "~/lib/segment";
import { openShareMeetingModal } from "~/meetingRecorder/ShareMeetingModal";
import Button from "~/ui/button";
import IconButton from "~/ui/button/IconButton";

import RequestAccessTooltip from "./RequestAccessTooltip";

import type { ShareButton_note$key } from "~/__generated__/ShareButton_note.graphql";

import styles from "./ShareButton.module.css";

const noteFragment = graphql`
	fragment ShareButton_note on Note {
		id
		noteType
		stream {
			id
		}
		...RequestAccessTooltip_note
	}
`;

export default function ShareButton({ note: noteKey }: { note: ShareButton_note$key }) {
	const note = useFragment(noteFragment, noteKey);

	const onSendNotesClick = useCallback(() => {
		dispatchAnalyticsTrack("Modal Opened", {
			/* eslint-disable camelcase */
			modal_id: "send_note",
			note_id: note.id,
			stream_id: note.stream.id,
			note_type: note.noteType,
			/* eslint-enable camelcase */
		});

		openShareMeetingModal(note.id, "Send");
	}, [note.id, note.noteType, note.stream.id]);

	const openRequestsAccessModal = React.useCallback(() => {
		dispatchAnalyticsTrack("Modal Opened", {
			/* eslint-disable camelcase */
			modal_id: "share_note",
			note_id: note.id,
			stream_id: note.stream.id,
			note_type: note.noteType,
			/* eslint-enable camelcase */
		});

		openShareMeetingModal(note.id, "Share");
	}, [note.id, note.noteType, note.stream.id]);

	return (
		<RequestAccessTooltip note={note} openRequestsAccessModal={openRequestsAccessModal}>
			<>
				<Button
					variant="tertiary"
					size="slim"
					className={styles.bigShareButton}
					onClick={onSendNotesClick}
					data-tour-tooltip-target="share-notes-button"
					data-test-id="note-header--send-notes"
				>
					<FontAwesomeIcon icon={faPaperPlane} />
					Share
				</Button>

				<IconButton
					icon={faPaperPlane}
					variant="tertiary"
					className={styles.smallShareButton}
					onClick={onSendNotesClick}
					title="Share"
					data-tour-tooltip-target="share-notes-button"
					data-test-id="note-header--send-notes"
				/>
			</>
		</RequestAccessTooltip>
	);
}
